import Divider from '@/components/Divider';
import { LogoIcon } from '@/components/Icons';
import Show from '@/components/Show';
import { useDimensions } from '@/hooks/useDimensions';
import { useSessionStore } from '@/hooks/useSessionStore';
import useEditorStore, { EditorOperatorEnum } from '@/state/editor-store';
import useGeneratorStore, { GeneratorTypeEnums, R3imagineStatusType } from '@/state/generator-store';
import { RoleEnum } from '@/types/global';
import useFlags from '@jenmusic/feature-flag';
import { modelVersion } from '@jenmusic/jen-config';
import { isEmpty } from 'lodash-es';
import React, { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useStyleFilter } from '../../hooks/useStyleFilter';
import { assistList, editJen2ModelList, editJen1ModelList, modelList, qualityList } from '../../utils';
import GenerateButton from '../GenerateButton';
import { AddFilterIcon } from './AddFilterIcon';
import CardSelector from './CardSelector';
import CreditCard from './CreditCard';
import DemoPowerBar from './DemoPowerBar';
import DurationInput from './DurationInput';
import EffectsButton from './EffectsButton';
import FolderSelectorCard from './FolderSelectorCard';
import MobileSimpleEffects from './MobileSimpleEffects';
import OldMobileSimpleEffects from './OldMobileSimpleEffects';
import SimplePromptInput from './SimplePromptInput';
import OldPromptInput from './SimplePromptInput/OldPromptInput';
import { cn } from '@/utils';

export const DefaultQualityValueMap = {
  [RoleEnum.Novice]: 'mp3',
  [RoleEnum.Dabbler]: 'wav',
  [RoleEnum.Soloist]: 'wav',
  [RoleEnum.Pro]: 'wav',
};

interface SimpleEffectsProps {
  generatorLoading: boolean | undefined;
}

const SimpleEffects: React.FC<SimpleEffectsProps> = ({ generatorLoading = false }) => {
  const { control, setIsGeneratorDrawerShow, isGeneratorDrawerShow, generatorType, formValues, r3ImageStatus } = useGeneratorStore(
    (state) => ({
      control: state.control,
      setIsGeneratorDrawerShow: state.setIsGeneratorDrawerShow,
      isGeneratorDrawerShow: state.isGeneratorDrawerShow,
      generatorType: state.generatorType,
      formValues: state.formValues,
      r3ImageStatus: state.r3ImageStatus,
    }),
  );

  const { session } = useSessionStore();

  const { trimLoading, operator, finalDuration } = useEditorStore((state) => ({
    trimLoading: state.trimLoading,
    operator: state.operator,
    finalDuration: state.finalDuration,
  }));

  const disabled = useMemo(() => {
    if (trimLoading || generatorLoading) {
      return true;
    }

    if (generatorType === GeneratorTypeEnums.EDITOR && (isEmpty(operator) || operator === EditorOperatorEnum.NONE)) {
      return true;
    }

    if (generatorType === GeneratorTypeEnums.R3IMAGINE && r3ImageStatus !== R3imagineStatusType.WaitingGenerate) {
      return true;
    }

    return false;
  }, [trimLoading, generatorType, generatorLoading, r3ImageStatus, operator, formValues?.prompt]);

  const { isMobileOrPad } = useDimensions();

  const { show: showStyleFilter, StyleFilterDialogComponent } = useStyleFilter();
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const sfAutoOpen = searchParams.get('sfAutoOpen') === 'true';
    if (!isMobileOrPad && sfAutoOpen) {
      showStyleFilter();
    }
  }, [location, isMobileOrPad]);

  const qualityDefaultValue = useMemo((): string => {
    if (session?.user?.plan?.key) {
      const value = DefaultQualityValueMap[session.user.plan.key as RoleEnum];
      return value;
    } else {
      return 'mp3';
    }
  }, [session?.user?.plan?.key]);

  const { showNewGenerator, showStyleFilters, showJen2 } = useFlags();

  if (control) {
    if (isMobileOrPad) {
      if (showNewGenerator) {
        return <MobileSimpleEffects />;
      }
      return <OldMobileSimpleEffects disabled={disabled} />;
    }

    return (
      <div
        className={cn(
          `h-[110px] w-full bg-[#2B2D2F] grid   overflow-x-auto gap-[1px]`,
          isGeneratorDrawerShow ? 'grid-cols-[100px_1fr_352px]' : 'grid-cols-[352px_1fr_352px]',
        )}
        style={{
          scrollbarWidth: 'none',
        }}
      >
        <div className="flex justify-around items-center bg-[#202223] pr-1">
          <div className="bg-[#202223] flex flex-col items-center justify-center">
            <LogoIcon className="object-cover" />
            <div className="flex w-[80px]">
              {showJen2 === true && (
                <span className="text-[0.6rem] text-white bg-[#333537] py-1 px-[0.6rem] w-full text-center">v{modelVersion}</span>
              )}
              {showJen2 === false && (
                <>
                  <span className="text-[0.6rem] text-white bg-[#333537] py-1 px-[0.6rem] w-full text-center">v1.6+</span>
                  <span className="text-[0.6rem] text-white bg-[#333537] py-1 px-[0.6rem]">BETA</span>
                </>
              )}
            </div>
          </div>
          <Show when={isGeneratorDrawerShow === false && !showNewGenerator}>
            <>
              <EffectsButton onClick={() => setIsGeneratorDrawerShow(!isGeneratorDrawerShow)} />
            </>
          </Show>
          <Show when={showNewGenerator && generatorType !== GeneratorTypeEnums.EDITOR}>
            <>
              <CreditCard />
            </>
          </Show>
        </div>
        <div className="bg-[#202223] text-white flex flex-1 items-center">
          <div className="flex-grow flex items-center mx-2 h-full">
            <div className="flex-1 flex items-center mx-1 h-24">
              <Show when={isGeneratorDrawerShow} fallback={showNewGenerator ? <SimplePromptInput /> : <OldPromptInput />}>
                <>
                  <CardSelector
                    name="model"
                    showName="MODEL"
                    options={generatorType === GeneratorTypeEnums.EDITOR ? (showJen2 ? editJen2ModelList : editJen1ModelList) : modelList}
                    showLabel={true}
                    showDropdown={true}
                    showVersionTag={true}
                  />
                  <CardSelector
                    name="quality"
                    showName="QUALITY"
                    options={qualityList}
                    defaultValue={qualityDefaultValue}
                    showDropdown={true}
                  />
                  <CardSelector name="assist" showName="ASSIST" options={assistList} />
                  <Show when={generatorType === GeneratorTypeEnums.TEXT}>
                    <DurationInput className="ml-2" theme="dark" />
                  </Show>
                  <Show when={generatorType === GeneratorTypeEnums.EDITOR}>
                    <DurationInput className="ml-2" theme="dark" disabled={true} initialValue={finalDuration} />
                  </Show>
                  <div className="hover: cursor-not-allowed">
                    <DemoPowerBar subScriptionPlanKey={session.user.plan.key as RoleEnum} />
                  </div>
                  <Divider border={{ direction: 'right', color: '#121213' }} />
                  <FolderSelectorCard />
                </>
              </Show>
            </div>
            <Show when={showStyleFilters}>
              <AddFilterIcon
                className="cursor-pointer"
                onClick={() => {
                  showStyleFilter();
                }}
              />
            </Show>
          </div>
        </div>
        <div
          className="generator_box flex h-full items-center shrink-0 min-w-[280px]"
          style={{
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
        >
          <GenerateButton type="submit" disabled={disabled} />
        </div>
        <StyleFilterDialogComponent />
      </div>
    );
  }
};

export default SimpleEffects;
